import { useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";
import PillarsGraphic from "./PillarsGraphic";

const useStyles = makeStyles({
  root: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.ink,
    backgroundImage: `linear-gradient(135deg, ${BRAND.ink} 0%, ${BRAND.purple} 60%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  topBar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "16px",
  },
  skipBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(255,255,255,0.06)",
    backgroundImage: "none",
    color: "rgba(255,255,255,0.85)",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.2)"),
    ...shorthands.borderRadius("4px"),
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.12)",
    },
  },
  stage: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "80px",
  },
  pageOneWrap: {
    textAlign: "center",
  },
  eyebrow: {
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    letterSpacing: "0.4em",
    textTransform: "uppercase",
    color: "rgba(247,168,35,0.85)",
    animationDuration: "600ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  },
  hero: {
    fontFamily: BRAND.fontAccent,
    color: BRAND.gold,
    fontSize: "clamp(2.5rem, 9vw, 6rem)",
    lineHeight: 1.05,
    fontWeight: 400,
    margin: 0,
    animationDuration: "700ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  heroSub: {
    marginTop: "8px",
    fontFamily: BRAND.fontAccent,
    color: "rgba(255,255,255,0.95)",
    fontSize: "clamp(1.6rem, 5vw, 2.4rem)",
    animationDuration: "1100ms",
    animationDelay: "300ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  mergedWrap: {
    width: "100%",
    maxWidth: "960px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "28px",
    animationDuration: "700ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  textBlock: {
    textAlign: "center",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.3rem, 3vw, 1.8rem)",
    lineHeight: 1.45,
    color: "rgba(255,255,255,0.95)",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  bold: {
    color: BRAND.gold,
    fontWeight: 700,
  },
  footer: {
    position: "absolute",
    insetInlineStart: 0,
    insetInlineEnd: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    paddingBottom: "24px",
  },
  footerInner: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  dots: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
  },
  dot: {
    height: "6px",
    width: "24px",
    backgroundColor: "rgba(255,255,255,0.25)",
    transitionProperty: "background-color",
    transitionDuration: "200ms",
    ...shorthands.borderRadius("999px"),
  },
  dotActive: {
    backgroundColor: BRAND.gold,
  },
  weiterBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.gold,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.gold),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: "#e89d20",
      ...shorthands.borderColor("#e89d20"),
    },
  },
  zurueckBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: "rgba(255,255,255,0.75)",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 500,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.2)"),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.06)",
      color: BRAND.white,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.3,
    },
  },
});

type Props = {
  companyName: string;
  onComplete: () => void;
};

const WelcomeFlow = ({ companyName, onComplete }: Props) => {
  const styles = useStyles();
  const [page, setPage] = useState(0);

  const next = () => {
    if (page === 1) onComplete();
    else setPage((p) => p + 1);
  };
  const prev = () => setPage((p) => Math.max(0, p - 1));
  const skipAll = () => onComplete();

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <button type="button" onClick={skipAll} className={styles.skipBtn}>
          Direkt zum Formular →
        </button>
      </div>
      <div className={styles.stage}>
        {page === 0 && (
          <div className={styles.pageOneWrap}>
            <div className={styles.eyebrow}>Risikoanalyse</div>
            <h1 className={styles.hero}>Analyse</h1>
            <div className={styles.heroSub}>{companyName || "Ihr Unternehmen"}</div>
          </div>
        )}
        {page === 1 && (
          <div className={styles.mergedWrap}>
            <div className={styles.textBlock}>
              <div>
                Zum <span className={styles.bold}>Kennenlernen</span> Ihres{" "}
                <span className={styles.bold}>Unternehmens</span> und zur{" "}
                <span className={styles.bold}>Ersteinschätzung</span> Ihrer{" "}
                <span className={styles.bold}>Risiken</span>
              </div>
            </div>
            <PillarsGraphic />
            <div className={styles.textBlock}>
              <div>
                und Ihres <span className={styles.bold}>Bedarfs</span> haben wir ein paar Fragen,
              </div>
              <div>
                bevor wir zu einem späteren Zeitpunkt noch in die Detailanalyse einsteigen.
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <button
            type="button"
            onClick={prev}
            className={styles.zurueckBtn}
            disabled={page === 0}
          >
            Zurück
          </button>
          <div className={styles.dots}>
            {[0, 1].map((i) => (
              <span
                key={i}
                className={mergeClasses(styles.dot, (i === page) && styles.dotActive)}
              />
            ))}
          </div>
          <button type="button" onClick={next} className={styles.weiterBtn}>
            {page === 1 ? "Zur Analyse" : "Weiter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeFlow;
